import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { compareSectorNames } from "@/lib/sectorSort";
import { ChevronDown, RotateCcw, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { RRebalanceNotesDialog } from "@/components/r-benchmark/RRebalanceNotesDialog";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface Fund { id: string; fund_name: string; fe_fund_name: string; fund_manager: string | null; fund_class: string | null; isin: string | null; ocf_pct: number | null; }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; }
interface SectorMapping { fund_id: string; sector_id: string; weighting: number; }
interface BenchmarkSectorWeight { sector_name: string; weight_pct: number; }
interface BMHouse { id: string; name: string; benchmark: number; }
interface BMAllocation { fund_house_id: string; sector_name: string; benchmark: number; weight_pct: number; }

interface HypoFund {
  id: string;            // synthetic id prefixed with "hypo:"
  fund_name: string;
  fund_manager: string | null;
  fund_class: string | null;
  isin: string | null;
  yield_pct: number | null;
  ocf_pct: number | null;
  weight_pct: number;    // hypothetical portfolio weight
  sectorWeights: Record<string, number>; // sector_id -> 0..1 fraction
}

const PORTFOLIOS = [1, 2, 3, 4, 5];

function AddHypoFundDialog({
  sectors,
  funds,
  onAdd,
}: {
  sectors: MasterSector[];
  funds: Fund[];
  onAdd: (f: HypoFund) => void;
}) {
  const [open, setOpen] = useState(false);
  const [isin, setIsin] = useState("");
  const [name, setName] = useState("");
  const [manager, setManager] = useState("");
  const [fundClass, setFundClass] = useState("");
  const [yieldPct, setYieldPct] = useState<string>("");
  const [ocfPct, setOcfPct] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [sectorPct, setSectorPct] = useState<Record<string, string>>({});
  const [matchedFundId, setMatchedFundId] = useState<string | null>(null);

  // Lookup ISIN in fund universe and prepopulate (checks ba_cbam_funds first, then investable_universe)
  useEffect(() => {
    const key = isin.trim().toUpperCase();
    if (!key) { setMatchedFundId(null); return; }
    let cancelled = false;
    const loadSectorWeights = async (fundId: string) => {
      const { data } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("sector_id, weighting, clarification_number")
        .eq("fund_id", fundId);
      if (cancelled || !data || data.length === 0) return;
      // Use the most recent clarification (highest clarification_number)
      const maxClar = Math.max(...data.map((r) => Number(r.clarification_number) || 1));
      const rows = data.filter((r) => (Number(r.clarification_number) || 1) === maxClar);
      const next: Record<string, string> = {};
      for (const r of rows) {
        const w = Number(r.weighting) || 0;
        // weighting is stored as fraction (0-1); show as percentage
        const pct = w <= 1.0001 ? w * 100 : w;
        if (pct > 0) next[r.sector_id as string] = pct.toFixed(2);
      }
      setSectorPct(next);
    };
    const loadYield = async (fundId: string, fallbackIsin?: string | null) => {
      const { data } = await supabase
        .from("ba_dist_fund_yields")
        .select("yield_pct")
        .eq("fund_id", fundId)
        .maybeSingle();
      if (cancelled) return;
      if (data?.yield_pct != null) {
        setYieldPct((prev) => prev || String(data.yield_pct));
        return;
      }
      // Fallback to FE asset breakdown yield by ISIN
      const isinKey = (fallbackIsin || key).toUpperCase();
      const { data: fe } = await supabase
        .from("fe_asset_breakdown")
        .select("yield_pct, as_of_date")
        .ilike("isin", isinKey)
        .order("as_of_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (fe?.yield_pct != null) setYieldPct((prev) => prev || String(fe.yield_pct));
    };
    const match = funds.find((f) => (f.isin || "").toUpperCase() === key);
    if (match) {
      setName(match.fund_name || "");
      setManager(match.fund_manager || "");
      setFundClass(match.fund_class || "");
      setOcfPct(match.ocf_pct != null ? String(match.ocf_pct) : "");
      setMatchedFundId(match.id);
      loadSectorWeights(match.id);
      loadYield(match.id, match.isin);
      return;
    }
    setMatchedFundId(null);
    (async () => {
      const { data } = await supabase
        .from("investable_universe")
        .select("fund_name, fund_manager, ocf")
        .ilike("isin", key)
        .limit(1)
        .maybeSingle();
      if (cancelled || !data) return;
      setName((prev) => prev || data.fund_name || "");
      setManager((prev) => prev || data.fund_manager || "");
      setOcfPct((prev) => prev || (data.ocf != null ? String(data.ocf) : ""));
      setMatchedFundId("universe");
      // Try to find a ba_cbam_funds row by ISIN for sector mappings + yield
      const { data: cbamFund } = await supabase
        .from("ba_cbam_funds")
        .select("id")
        .ilike("isin", key)
        .limit(1)
        .maybeSingle();
      if (cbamFund?.id) {
        loadSectorWeights(cbamFund.id);
        loadYield(cbamFund.id, key);
      } else {
        // No ba_cbam_funds row — try FE breakdown directly
        const { data: fe } = await supabase
          .from("fe_asset_breakdown")
          .select("yield_pct, as_of_date")
          .ilike("isin", key)
          .order("as_of_date", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!cancelled && fe?.yield_pct != null) {
          setYieldPct((prev) => prev || String(fe.yield_pct));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isin, funds]);



  const totalSector = Object.values(sectorPct).reduce(
    (a, v) => a + (parseFloat(v) || 0),
    0
  );

  const reset = () => {
    setIsin(""); setName(""); setManager(""); setFundClass(""); setYieldPct(""); setOcfPct(""); setWeight("");
    setSectorPct({}); setMatchedFundId(null);
  };

  const submit = () => {
    if (!name.trim()) return;
    if (Math.abs(totalSector - 100) > 0.5) return;
    const sectorWeights: Record<string, number> = {};
    for (const [k, v] of Object.entries(sectorPct)) {
      const n = parseFloat(v);
      if (n && n > 0) sectorWeights[k] = n / 100;
    }
    onAdd({
      id: `hypo:${crypto.randomUUID()}`,
      fund_name: name.trim(),
      fund_manager: manager.trim() || null,
      fund_class: fundClass.trim() || null,
      isin: isin.trim().toUpperCase() || null,
      yield_pct: yieldPct === "" ? null : parseFloat(yieldPct),
      ocf_pct: ocfPct === "" ? null : parseFloat(ocfPct),
      weight_pct: weight === "" ? 0 : parseFloat(weight),
      sectorWeights,
    });
    reset();
    setOpen(false);
  };


  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-3 w-3 mr-1" /> Add hypothetical fund
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add hypothetical fund</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">ISIN (lookup fund universe)</Label>
            <Input
              value={isin}
              onChange={(e) => setIsin(e.target.value)}
              placeholder="Type an ISIN to auto-fill from the universe"
              className="h-8 text-xs font-mono"
            />
            {isin.trim() && (
              <div className={cn("text-[10px] mt-1", matchedFundId ? "text-emerald-600" : "text-muted-foreground")}>
                {matchedFundId ? "Matched — fields prefilled below" : "No match in fund universe"}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fund name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs" />
            </div>

            <div>
              <Label className="text-xs">Fund manager</Label>
              <Input value={manager} onChange={(e) => setManager(e.target.value)} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Fund class</Label>
              <Input value={fundClass} onChange={(e) => setFundClass(e.target.value)} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Yield %</Label>
              <Input
                type="number" step="0.01" value={yieldPct}
                onChange={(e) => setYieldPct(e.target.value)} className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">OCF %</Label>
              <Input
                type="number" step="0.01" value={ocfPct}
                onChange={(e) => setOcfPct(e.target.value)} className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Portfolio weight %</Label>
              <Input
                type="number" step="0.01" value={weight}
                onChange={(e) => setWeight(e.target.value)} className="h-8 text-xs"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Sector weightings (must total 100%)</Label>
              <Badge variant={Math.abs(totalSector - 100) < 0.5 ? "default" : "destructive"}>
                {totalSector.toFixed(2)}%
              </Badge>
            </div>
            <div className="border rounded-md max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {[...sectors].sort((a, b) => compareSectorNames(a.name, b.name)).map((s, idx) => (
                    <tr key={s.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                      <td className="px-2 py-1">
                        <div className="font-medium">{s.name}</div>
                      </td>
                      <td className="p-2 w-28">
                        <Input
                          type="number" step="0.01"
                          value={sectorPct[s.id] ?? ""}
                          onChange={(e) =>
                            setSectorPct((prev) => ({ ...prev, [s.id]: e.target.value }))
                          }
                          className="h-7 text-xs text-right font-mono"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!name.trim() || Math.abs(totalSector - 100) > 0.5}
          >
            Add fund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PortfolioCalculator({ portfolio }: { portfolio: number }) {
  const queryClient = useQueryClient();
  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors-calc"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name, sector_group, display_order")
        .order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds-calc"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_funds")
        .select("id, fund_name, fe_fund_name, fund_manager, fund_class, isin, ocf_pct")
        .order("fund_name");
      // Note: do not filter by is_current — a fund flagged legacy may still hold
      // weight in another R portfolio, and hiding it here would drop it from view.
      if (error) throw error;
      return data as Fund[];
    },
  });

  const { data: feYields } = useQuery({
    queryKey: ["fe-asset-breakdown-yields"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("isin, yield_pct, as_of_date")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const m = new Map<string, number>();
      for (const r of (data || []) as { isin: string; yield_pct: number | null }[]) {
        if (!m.has(r.isin) && r.yield_pct != null) m.set(r.isin.toUpperCase(), Number(r.yield_pct));
      }
      return m;
    },
  });

  const { data: storedYields } = useQuery({
    queryKey: ["ba-dist-fund-yields-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_dist_fund_yields")
        .select("fund_id, yield_pct");
      if (error) throw error;
      const m = new Map<string, number>();
      for (const r of (data || []) as { fund_id: string; yield_pct: number | null }[]) {
        if (r.yield_pct != null) m.set(r.fund_id, Number(r.yield_pct));
      }
      return m;
    },
  });

  const { data: weights } = useQuery({
    queryKey: ["ba-r-fund-weights-calc", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_r_cbam_fund_weights")
        .select("fund_id, portfolio, weight_pct, effective_date")
        .eq("portfolio", portfolio)
        .order("effective_date", { ascending: false });
      if (error) throw error;
      // Keep only the latest effective_date row per fund_id
      const latest = new Map<string, FundWeight>();
      for (const row of (data || []) as (FundWeight & { effective_date: string })[]) {
        if (!latest.has(row.fund_id)) latest.set(row.fund_id, row);
      }
      return Array.from(latest.values()) as FundWeight[];
    },
  });

  const { data: mappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings-calc"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("fund_id, sector_id, weighting");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: benchmarkWeights } = useQuery({
    queryKey: ["bm-allocations-by-benchmark-active", portfolio],
    queryFn: async () => {
      const { data: houses, error: hErr } = await supabase
        .from("bm_fund_houses")
        .select("id")
        .eq("benchmark", portfolio)
        .eq("is_active", true);
      if (hErr) throw hErr;
      const activeIds = (houses || []).map((h) => h.id);
      if (activeIds.length === 0) return [];
      const { data, error } = await supabase
        .from("bm_allocations")
        .select("fund_house_id, sector_name, benchmark, weight_pct")
        .eq("benchmark", portfolio)
        .in("fund_house_id", activeIds);
      if (error) throw error;
      const sums = new Map<string, number>();
      for (const row of data || []) {
        sums.set(row.sector_name, (sums.get(row.sector_name) || 0) + Number(row.weight_pct));
      }
      const denom = activeIds.length;
      return Array.from(sums.entries()).map(([sector_name, total]) => ({
        sector_name,
        weight_pct: total / denom,
      })) as { sector_name: string; weight_pct: number }[];
    },
  });


  const { data: bmHouses } = useQuery({
    queryKey: ["bm-fund-houses", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bm_fund_houses")
        .select("id, name, benchmark")
        .eq("benchmark", portfolio)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as BMHouse[];
    },
  });

  const { data: bmAllocations } = useQuery({
    queryKey: ["bm-allocations-houses", portfolio],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bm_allocations")
        .select("fund_house_id, sector_name, benchmark, weight_pct")
        .eq("benchmark", portfolio);
      if (error) throw error;
      return data as BMAllocation[];
    },
  });

  const [hypothetical, setHypothetical] = useState<Record<string, number>>({});
  const [selectedHouses, setSelectedHouses] = useState<string[]>([]);
  const [hypoFunds, setHypoFunds] = useState<HypoFund[]>([]);

  useEffect(() => {
    if (weights) {
      const init: Record<string, number> = {};
      for (const w of weights) init[w.fund_id] = Number(w.weight_pct);
      setHypothetical(init);
    }
  }, [weights]);

  // Default to all BM houses selected once they load
  useEffect(() => {
    if (bmHouses && bmHouses.length > 0 && selectedHouses.length === 0) {
      setSelectedHouses(bmHouses.map((h) => h.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bmHouses]);

  // Seed hypothetical weights for newly-added hypo funds
  useEffect(() => {
    setHypothetical((prev) => {
      const next = { ...prev };
      for (const hf of hypoFunds) {
        if (next[hf.id] === undefined) next[hf.id] = hf.weight_pct;
      }
      return next;
    });
  }, [hypoFunds]);

  const fundsInPortfolio = useMemo(() => {
    if (!funds || !weights) return [];
    const ids = new Set(weights.filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id));
    const real = funds.filter((f) => ids.has(f.id) || (hypothetical[f.id] || 0) > 0);
    const hypoAsFund: Fund[] = hypoFunds.map((h) => ({
      id: h.id,
      fund_name: h.fund_name,
      fe_fund_name: h.fund_name,
      fund_manager: h.fund_manager,
      fund_class: h.fund_class,
      isin: null,
      ocf_pct: h.ocf_pct,
    }));
    const ci = (a: string | null, b: string | null) =>
      (a || "").localeCompare(b || "", undefined, { sensitivity: "base" });
    return [...real, ...hypoAsFund].sort((a, b) => {
      const m = ci(a.fund_manager, b.fund_manager);
      if (m !== 0) return m;
      const n = ci(a.fund_name, b.fund_name);
      if (n !== 0) return n;
      return ci(a.fund_class, b.fund_class);
    });
  }, [funds, weights, hypothetical, hypoFunds]);

  const totalHypothetical = useMemo(
    () => fundsInPortfolio.reduce((acc, f) => acc + (hypothetical[f.id] || 0), 0),
    [fundsInPortfolio, hypothetical]
  );

  const yieldForFund = (fundId: string): number | null => {
    if (fundId.startsWith("hypo:")) {
      return hypoFunds.find((h) => h.id === fundId)?.yield_pct ?? null;
    }
    const stored = storedYields?.get(fundId);
    if (stored != null) return stored;
    const f = funds?.find((x) => x.id === fundId);
    if (!f?.isin) return null;
    return feYields?.get(f.isin.toUpperCase()) ?? null;
  };

  const ocfForFund = (fundId: string): number | null => {
    if (fundId.startsWith("hypo:")) {
      return hypoFunds.find((h) => h.id === fundId)?.ocf_pct ?? null;
    }
    const f = funds?.find((x) => x.id === fundId);
    return f?.ocf_pct == null ? null : Number(f.ocf_pct);
  };

  const weightedMetric = (
    valueAt: (id: string) => number | null,
    wAt: (fundId: string) => number,
  ): number | null => {
    let num = 0, den = 0;
    for (const f of fundsInPortfolio) {
      const w = wAt(f.id);
      const v = valueAt(f.id);
      if (v == null || !w) continue;
      num += w * v;
      den += w;
    }
    return den > 0 ? num / den : null;
  };

  const currentWeightAt = (id: string) => Number(weights?.find((w) => w.fund_id === id)?.weight_pct || 0);
  const hypoWeightAt = (id: string) => hypothetical[id] || 0;

  const currentYield = useMemo(
    () => weightedMetric(yieldForFund, currentWeightAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weights, fundsInPortfolio, feYields, funds, hypoFunds]
  );
  const hypotheticalYield = useMemo(
    () => weightedMetric(yieldForFund, hypoWeightAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hypothetical, fundsInPortfolio, feYields, funds, hypoFunds]
  );
  const currentOCF = useMemo(
    () => weightedMetric(ocfForFund, currentWeightAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weights, fundsInPortfolio, funds, hypoFunds]
  );
  const hypotheticalOCF = useMemo(
    () => weightedMetric(ocfForFund, hypoWeightAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hypothetical, fundsInPortfolio, funds, hypoFunds]
  );

  const hypothetiicalMappingsForHypo = useMemo(() => {
    // Convert HypoFund sector weights into SectorMapping-like entries
    const out: SectorMapping[] = [];
    for (const h of hypoFunds) {
      for (const [sid, w] of Object.entries(h.sectorWeights)) {
        out.push({ fund_id: h.id, sector_id: sid, weighting: w });
      }
    }
    return out;
  }, [hypoFunds]);

  const hypotheticalBySector = useMemo(() => {
    const out = new Map<string, number>();
    const all = [...(mappings || []), ...hypothetiicalMappingsForHypo];
    for (const f of fundsInPortfolio) {
      const fw = hypothetical[f.id] || 0;
      if (!fw) continue;
      const fundMappings = all.filter((m) => m.fund_id === f.id);
      for (const m of fundMappings) {
        out.set(m.sector_id, (out.get(m.sector_id) || 0) + fw * Number(m.weighting));
      }
    }
    return out;
  }, [fundsInPortfolio, hypothetical, mappings, hypothetiicalMappingsForHypo]);

  const benchmarkBySectorName = useMemo(() => {
    const out = new Map<string, number>();
    if (!benchmarkWeights) return out;
    for (const b of benchmarkWeights) {
      out.set(b.sector_name, (out.get(b.sector_name) || 0) + Number(b.weight_pct));
    }
    return out;
  }, [benchmarkWeights]);

  const bmHouseMeanBySectorName = useMemo(() => {
    const out = new Map<string, number>();
    if (!bmAllocations || selectedHouses.length === 0) return out;
    const filtered = bmAllocations.filter((a) => selectedHouses.includes(a.fund_house_id));
    if (filtered.length === 0) return out;
    const sums = new Map<string, number>();
    for (const a of filtered) {
      sums.set(a.sector_name, (sums.get(a.sector_name) || 0) + Number(a.weight_pct));
    }
    for (const [k, v] of sums) out.set(k, v / selectedHouses.length);
    return out;
  }, [bmAllocations, selectedHouses]);

  const updateWeight = (fundId: string, value: string) => {
    const v = value === "" ? 0 : parseFloat(value);
    setHypothetical((prev) => ({ ...prev, [fundId]: isNaN(v) ? 0 : v }));
  };

  const resetToCurrent = () => {
    if (weights) {
      const init: Record<string, number> = {};
      for (const w of weights) init[w.fund_id] = Number(w.weight_pct);
      setHypothetical(init);
      setHypoFunds([]);
    }
  };

  const toggleHouse = (id: string) =>
    setSelectedHouses((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const removeHypoFund = (id: string) => {
    setHypoFunds((prev) => prev.filter((h) => h.id !== id));
    setHypothetical((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const [commitOpen, setCommitOpen] = useState(false);
  const [commitDate, setCommitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [committing, setCommitting] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDate, setNotesDate] = useState<string>("");

  const portfolioCol = `portfolio_${portfolio}` as const;
  const includedCol = `${portfolioCol}_included` as const;
  const pctCol = `${portfolioCol}_pct` as const;

  const proposedTrades = useMemo(() => {
    const out: { fundId: string; fund_name: string; current: number; hypothetical: number; diff: number; isHypo: boolean }[] = [];
    for (const f of fundsInPortfolio) {
      const isHypo = f.id.startsWith("hypo:");
      const current = isHypo ? 0 : Number(weights?.find((w) => w.fund_id === f.id)?.weight_pct || 0);
      const hyp = Number(hypothetical[f.id] || 0);
      const diff = +(hyp - current).toFixed(4);
      if (Math.abs(diff) >= 0.005) {
        out.push({ fundId: f.id, fund_name: f.fund_name, current, hypothetical: hyp, diff, isHypo });
      }
    }
    return out;
  }, [fundsInPortfolio, weights, hypothetical]);

  const commitRebalance = async () => {
    if (!commitDate) { toast.error("Please pick a rebalance date"); return; }
    if (Math.abs(totalHypothetical - 100) > 0.05) {
      toast.error(`Hypothetical weights must total 100% (currently ${totalHypothetical.toFixed(2)}%)`);
      return;
    }
    if (proposedTrades.length === 0) { toast.info("No changes to commit"); return; }
    setCommitting(true);
    try {
      const batchId = crypto.randomUUID();

      // 1) Materialise hypothetical funds into ba_cbam_funds + sector mappings so they
      //    become real universe members (no more "Unknown" rows in the trade log).
      const hypoIdToRealId = new Map<string, string>();
      const hypoToCreate = proposedTrades.filter((t) => t.isHypo);
      for (const t of hypoToCreate) {
        const hf = hypoFunds.find((h) => h.id === t.fundId);
        if (!hf) continue;
        const fullName = [hf.fund_manager, hf.fund_name, hf.fund_class].filter(Boolean).join(" ").trim() || hf.fund_name;

        const fundPayload = {
          fund_name: hf.fund_name,
          fe_fund_name: fullName,
          fund_manager: hf.fund_manager,
          fund_class: hf.fund_class,
          isin: hf.isin ?? null,
          ocf_pct: hf.ocf_pct,
          is_current: true,
        };

        // Reuse an existing ba_cbam_funds row if one already matches (by fe_fund_name first, then ISIN).
        // fe_fund_name is unique, so it must be the source of truth for avoiding duplicate-key failures.
        let realId: string | null = null;
        const { data: byName } = await supabase
          .from("ba_cbam_funds")
          .select("id")
          .eq("fe_fund_name", fullName)
          .limit(1)
          .maybeSingle();
        if (byName?.id) realId = byName.id as string;

        if (!realId && hf.isin) {
          const { data: byIsin } = await supabase
            .from("ba_cbam_funds")
            .select("id")
            .ilike("isin", hf.isin)
            .limit(1)
            .maybeSingle();
          if (byIsin?.id) realId = byIsin.id as string;
        }

        if (!realId) {
          const { data: inserted, error: insErr } = await supabase
            .from("ba_cbam_funds")
            .upsert(fundPayload, { onConflict: "fe_fund_name" })
            .select("id")
            .single();
          if (insErr) throw insErr;
          realId = inserted.id as string;
        } else {
          // Reactivate and refresh editable fields from the dialog values
          const { error: uFundErr } = await supabase
            .from("ba_cbam_funds")
            .update(fundPayload)
            .eq("id", realId);
          if (uFundErr?.code === "23505") {
            const { data: conflictFund, error: conflictErr } = await supabase
              .from("ba_cbam_funds")
              .upsert(fundPayload, { onConflict: "fe_fund_name" })
              .select("id")
              .single();
            if (conflictErr) throw conflictErr;
            realId = conflictFund.id as string;
          } else if (uFundErr) {
            console.warn("ba_cbam_funds update failed", uFundErr.message);
          }
        }

        hypoIdToRealId.set(t.fundId, realId);

        // Only insert sector mappings if none exist for this fund yet
        const { data: existingMaps } = await supabase
          .from("ba_cbam_fund_sector_mappings")
          .select("sector_id")
          .eq("fund_id", realId)
          .limit(1);
        if (!existingMaps || existingMaps.length === 0) {
          const mappingRows = Object.entries(hf.sectorWeights)
            .filter(([, w]) => w > 0)
            .map(([sector_id, w]) => ({ fund_id: realId!, clarification_number: 1, sector_id, weighting: w }));
          if (mappingRows.length > 0) {
            const { error: mErr } = await supabase.from("ba_cbam_fund_sector_mappings").insert(mappingRows);
            if (mErr) throw mErr;
          }
        }


        // Persist yield to ba_dist_fund_yields so it is retrievable post-commit
        if (hf.yield_pct != null) {
          const { error: yErr } = await supabase
            .from("ba_dist_fund_yields")
            .upsert({ fund_id: realId, yield_pct: hf.yield_pct }, { onConflict: "fund_id" });
          if (yErr) console.warn("yield save failed", yErr.message);
        }

        // Also add to the Investable Universe if not already there (match on ISIN, else name+manager)
        let existsInUniverse = false;
        if (hf.isin) {
          const { data: byIsin } = await supabase
            .from("investable_universe")
            .select("id")
            .ilike("isin", hf.isin)
            .limit(1);
          existsInUniverse = !!(byIsin && byIsin.length);
        }
        if (!existsInUniverse) {
          const { data: byName } = await supabase
            .from("investable_universe")
            .select("id")
            .ilike("fund_name", fullName)
            .limit(1);
          existsInUniverse = !!(byName && byName.length);
        }
        if (!existsInUniverse) {
          const { error: uErr } = await supabase.from("investable_universe").insert({
            source: "Wrap",
            fund_name: fullName,
            fund_manager: hf.fund_manager,
            isin: hf.isin,
            ocf: hf.ocf_pct,
          });
          if (uErr) console.warn("investable_universe insert failed", uErr.message);
        }
      }

      // 2) Merge trades into existing same-day rows per fund, else insert new
      const resolvedTrades = proposedTrades.map((t) => ({
        ...t,
        realFundId: (t.isHypo ? hypoIdToRealId.get(t.fundId) : t.fundId) as string,
      }));
      const fundIds = resolvedTrades.map((t) => t.realFundId);
      const { data: existingSameDay, error: exErr } = await supabase
        .from("ba_r_trades")
        .select("id, fund_id, direction, notes, portfolio_1_included, portfolio_2_included, portfolio_3_included, portfolio_4_included, portfolio_5_included, portfolio_1_pct, portfolio_2_pct, portfolio_3_pct, portfolio_4_pct, portfolio_5_pct")
        .eq("trade_date", commitDate)
        .in("fund_id", fundIds);
      if (exErr) throw exErr;
      const existingByFund = new Map<string, Record<string, unknown>>();
      (existingSameDay ?? []).forEach((r: Record<string, unknown>) => existingByFund.set(r.fund_id as string, r));

      const toInsert: Record<string, unknown>[] = [];
      for (const t of resolvedTrades) {
        const existing = existingByFund.get(t.realFundId);
        const dir = t.diff >= 0 ? "BUY" : "SELL";
        if (existing) {
          const patch: Record<string, unknown> = {
            [includedCol]: true,
            [pctCol]: Math.abs(t.diff),
          };
          // Append portfolio tag to notes if not already noted
          const prevNotes = (existing.notes as string) ?? "";
          if (!prevNotes.includes(`R${portfolio}`)) {
            patch.notes = prevNotes
              ? `${prevNotes}; Hypothetical calculator commit (R${portfolio})`
              : `Hypothetical calculator commit (R${portfolio})`;
          }
          // If direction conflicts across portfolios, mark as MIXED
          if (existing.direction && existing.direction !== dir) patch.direction = "MIXED";
          const { error: uErr2 } = await supabase
            .from("ba_r_trades")
            .update(patch as never)
            .eq("id", existing.id as string);
          if (uErr2) throw uErr2;
        } else {
          const row: Record<string, unknown> = {
            trade_date: commitDate,
            fund_id: t.realFundId,
            direction: dir,
            trade_type: "rebalance",
            batch_id: batchId,
            notes: `Hypothetical calculator commit (R${portfolio})`,
            portfolio_1_included: false, portfolio_1_pct: 0,
            portfolio_2_included: false, portfolio_2_pct: 0,
            portfolio_3_included: false, portfolio_3_pct: 0,
            portfolio_4_included: false, portfolio_4_pct: 0,
            portfolio_5_included: false, portfolio_5_pct: 0,
          };
          row[includedCol] = true;
          row[pctCol] = Math.abs(t.diff);
          toInsert.push(row);
        }
      }
      if (toInsert.length > 0) {
        const { error: tErr } = await supabase.from("ba_r_trades").insert(toInsert as never);
        if (tErr) throw tErr;
      }

      // 3) Upsert new weights for all funds (real + newly-materialised hypos)
      const weightRows = proposedTrades.map((t) => ({
        fund_id: (t.isHypo ? hypoIdToRealId.get(t.fundId) : t.fundId) as string,
        portfolio,
        weight_pct: t.hypothetical,
        effective_date: commitDate,
      }));
      if (weightRows.length > 0) {
        const { error: wErr } = await supabase
          .from("ba_r_cbam_fund_weights")
          .upsert(weightRows, { onConflict: "fund_id,portfolio,effective_date" });
        if (wErr) throw wErr;
      }

      toast.success(
        `Committed ${proposedTrades.length} trades for R${portfolio}` +
          (hypoToCreate.length ? ` (${hypoToCreate.length} hypothetical fund${hypoToCreate.length > 1 ? "s" : ""} added to the fund universe)` : "")
      );
      queryClient.invalidateQueries({ queryKey: ["ba-r-fund-weights-calc", portfolio] });
      queryClient.invalidateQueries({ queryKey: ["ba-r-trades"] });
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-active"] });
      setHypoFunds([]);
      setCommitOpen(false);

      // Trigger a: if all R1–R5 have trades on this date, offer AI-drafted notes
      try {
        const { data: dayTrades } = await supabase
          .from("ba_r_trades")
          .select("portfolio_1_included, portfolio_2_included, portfolio_3_included, portfolio_4_included, portfolio_5_included")
          .eq("trade_date", commitDate);
        const covered = new Set<number>();
        (dayTrades ?? []).forEach((r: Record<string, boolean>) => {
          [1, 2, 3, 4, 5].forEach((n) => { if (r[`portfolio_${n}_included`]) covered.add(n); });
        });
        if (covered.size === 5) {
          setNotesDate(commitDate);
          setNotesOpen(true);
        }
      } catch (e) {
        console.warn("R rebalance completeness check failed", e);
      }
    } catch (e: unknown) {
      console.error("Commit rebalance failed:", e);
      const anyE = e as { message?: string; details?: string; hint?: string; code?: string };
      const msg =
        e instanceof Error
          ? e.message
          : anyE?.message
            ? [anyE.message, anyE.details, anyE.hint, anyE.code ? `(code ${anyE.code})` : ""].filter(Boolean).join(" — ")
            : (() => { try { return JSON.stringify(e); } catch { return String(e); } })();
      toast.error(`Commit failed: ${msg || "unknown error"}`);

    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Hypothetical Fund Weights — R{portfolio}</CardTitle>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Badge variant={Math.abs(totalHypothetical - 100) < 0.05 ? "default" : "destructive"}>
              Total: {totalHypothetical.toFixed(2)}%
            </Badge>
            <AddHypoFundDialog
              sectors={sectors || []}
              funds={funds || []}
              onAdd={(f) => setHypoFunds((prev) => [...prev, f])}
            />

            <Button variant="outline" size="sm" onClick={resetToCurrent}>
              <RotateCcw className="h-3 w-3 mr-1" /> Reset
            </Button>
            <Dialog open={commitOpen} onOpenChange={setCommitOpen}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={proposedTrades.length === 0 || Math.abs(totalHypothetical - 100) > 0.05}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Commit rebalance
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Commit hypothetical to rebalance — R{portfolio}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">Rebalance date</Label>
                      <Input type="date" value={commitDate} onChange={(e) => setCommitDate(e.target.value)} className="h-8 text-xs" />
                    </div>
                    <div className="text-xs text-muted-foreground pb-1">
                      {proposedTrades.length} trade{proposedTrades.length === 1 ? "" : "s"} will be logged
                    </div>
                  </div>
                  <div className="border rounded-md max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/60">
                        <tr>
                          <th className="text-left p-2">Fund</th>
                          <th className="text-right p-2 w-20">Current %</th>
                          <th className="text-right p-2 w-20">Target %</th>
                          <th className="text-right p-2 w-20">Δ</th>
                          <th className="text-center p-2 w-16">Dir</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proposedTrades.map((t) => (
                          <tr key={t.fundId} className="border-t">
                            <td className="p-2">
                              {t.fund_name}
                              {t.isHypo && <Badge variant="secondary" className="ml-1 text-[9px] py-0 px-1">HYPO</Badge>}
                            </td>
                            <td className="p-2 text-right font-mono">{t.current.toFixed(2)}</td>
                            <td className="p-2 text-right font-mono">{t.hypothetical.toFixed(2)}</td>
                            <td className={cn("p-2 text-right font-mono", t.diff >= 0 ? "text-emerald-600" : "text-red-600")}>
                              {t.diff >= 0 ? "+" : ""}{t.diff.toFixed(2)}
                            </td>
                            <td className="p-2 text-center">
                              <Badge variant={t.diff >= 0 ? "default" : "destructive"} className="text-[10px]">
                                {t.diff >= 0 ? "BUY" : "SELL"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Trades will be inserted into the R trade log (sharing one batch) and portfolio weights in
                    <code className="mx-1">ba_r_cbam_fund_weights</code>
                    will be upserted as of the rebalance date. Any hypothetical funds will be added to the
                    fund universe (with their sector mappings) so they appear as named entries in the trade log.
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCommitOpen(false)} disabled={committing}>Cancel</Button>
                  <Button onClick={commitRebalance} disabled={committing}>
                    {committing ? "Committing…" : "Confirm commit"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border-b bg-muted/30">
            <div className="text-xs">
              <div className="text-muted-foreground">Current yield</div>
              <div className="font-mono text-base font-semibold">{currentYield != null ? `${currentYield.toFixed(2)}%` : "—"}</div>
            </div>
            <div className="text-xs">
              <div className="text-muted-foreground">Hypothetical yield</div>
              <div className="font-mono text-base font-semibold">
                {hypotheticalYield != null ? `${hypotheticalYield.toFixed(2)}%` : "—"}
                {currentYield != null && hypotheticalYield != null && (
                  <span className={cn("ml-2 text-xs", hypotheticalYield - currentYield >= 0 ? "text-emerald-600" : "text-red-600")}>
                    ({hypotheticalYield - currentYield >= 0 ? "+" : ""}{(hypotheticalYield - currentYield).toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>
            <div className="text-xs">
              <div className="text-muted-foreground">Current OCF</div>
              <div className="font-mono text-base font-semibold">{currentOCF != null ? `${currentOCF.toFixed(2)}%` : "—"}</div>
            </div>
            <div className="text-xs">
              <div className="text-muted-foreground">Hypothetical OCF</div>
              <div className="font-mono text-base font-semibold">
                {hypotheticalOCF != null ? `${hypotheticalOCF.toFixed(2)}%` : "—"}
                {currentOCF != null && hypotheticalOCF != null && (
                  <span className={cn("ml-2 text-xs", hypotheticalOCF - currentOCF <= 0 ? "text-emerald-600" : "text-red-600")}>
                    ({hypotheticalOCF - currentOCF >= 0 ? "+" : ""}{(hypotheticalOCF - currentOCF).toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr className="border-b">
                <th className="text-left p-3 font-medium text-muted-foreground">Fund House</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-24">Current %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-24">Δ %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-28">Hypothetical %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-20">Yield %</th>
                <th className="text-right p-3 font-medium text-muted-foreground w-20">OCF %</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {fundsInPortfolio.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-muted-foreground text-xs">
                    No funds with weight in R{portfolio}.
                  </td>
                </tr>
              )}
              {fundsInPortfolio.map((f, idx) => {
                const isHypo = f.id.startsWith("hypo:");
                const current = isHypo
                  ? 0
                  : weights?.find((w) => w.fund_id === f.id)?.weight_pct || 0;
                const y = yieldForFund(f.id);
                const ocf = ocfForFund(f.id);
                return (
                  <tr key={f.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs text-muted-foreground">
                      {f.fund_manager || "—"}
                      {isHypo && (
                        <Badge variant="secondary" className="ml-1 text-[9px] py-0 px-1">HYPO</Badge>
                      )}
                    </td>
                    <td className="p-3 text-xs font-medium">{f.fund_name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{f.fund_class || "—"}</td>
                    <td className="p-3 text-right text-xs font-mono text-muted-foreground">
                      {Number(current).toFixed(2)}
                    </td>
                    <td className="p-2 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={
                          hypothetical[f.id] === undefined
                            ? ""
                            : Number((hypothetical[f.id] - Number(current)).toFixed(4))
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "" || raw === "-") {
                            updateWeight(f.id, String(Number(current)));
                            return;
                          }
                          const d = parseFloat(raw);
                          if (isNaN(d)) return;
                          updateWeight(f.id, String(Number(current) + d));
                        }}
                        className={cn(
                          "h-8 text-xs text-right font-mono",
                          (hypothetical[f.id] ?? Number(current)) - Number(current) > 0 && "text-emerald-600",
                          (hypothetical[f.id] ?? Number(current)) - Number(current) < 0 && "text-red-600",
                        )}
                        placeholder="0"
                      />
                    </td>
                    <td className="p-2 text-right">
                      <Input
                        type="number"
                        step="0.01"
                        value={hypothetical[f.id] ?? ""}
                        onChange={(e) => updateWeight(f.id, e.target.value)}
                        className="h-8 text-xs text-right font-mono"
                      />
                    </td>
                    <td className="p-3 text-right text-xs font-mono">
                      {y != null ? y.toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="p-3 text-right text-xs font-mono">
                      {ocf != null ? ocf.toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="p-1 text-center">
                      {isHypo && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeHypoFund(f.id)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {fundsInPortfolio.length > 0 && (() => {
              const currentTotal = fundsInPortfolio.reduce(
                (acc, f) =>
                  acc +
                  (f.id.startsWith("hypo:")
                    ? 0
                    : Number(weights?.find((w) => w.fund_id === f.id)?.weight_pct || 0)),
                0,
              );
              const hypoOk = Math.abs(totalHypothetical - 100) < 0.05;
              const deltaTotal = totalHypothetical - currentTotal;
              return (
                <tfoot className="sticky bottom-0 bg-muted/80 backdrop-blur">
                  <tr className="border-t-2 font-semibold">
                    <td className="p-3 text-xs" colSpan={3}>Total</td>
                    <td className="p-3 text-right text-xs font-mono text-muted-foreground">
                      {currentTotal.toFixed(2)}
                    </td>
                    <td className={cn("p-3 text-right text-xs font-mono", Math.abs(deltaTotal) < 0.05 ? "text-emerald-600" : "text-red-600")}>
                      {deltaTotal >= 0 ? "+" : ""}{deltaTotal.toFixed(2)}
                    </td>
                    <td className={cn("p-3 text-right text-xs font-mono", hypoOk ? "text-emerald-600" : "text-red-600")}>
                      {totalHypothetical.toFixed(2)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              );
            })()}
          </table>

        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Resulting Sector Allocation</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Compare vs BM houses:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  {selectedHouses.length === 0
                    ? "Select fund houses"
                    : selectedHouses.length === (bmHouses?.length || 0)
                    ? `All (${selectedHouses.length})`
                    : `${selectedHouses.length} selected`}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto" align="start">
                {(bmHouses || []).length === 0 && (
                  <p className="text-xs text-muted-foreground p-2">
                    No BM fund houses configured for R{portfolio}.
                  </p>
                )}
                {(bmHouses || []).map((h) => (
                  <label
                    key={h.id}
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded-md cursor-pointer text-xs"
                  >
                    <Checkbox
                      checked={selectedHouses.includes(h.id)}
                      onCheckedChange={() => toggleHouse(h.id)}
                    />
                    <span>{h.name}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setSelectedHouses(bmHouses?.map((h) => h.id) || [])}
            >
              All
            </Button>
            {selectedHouses.length > 0 && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSelectedHouses([])}>
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr className="border-b">
                <th className="text-left p-3 font-medium text-muted-foreground">CBAM Sector</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Hypothetical %</th>
                <th className="text-right p-3 font-medium text-muted-foreground">vs Benchmark</th>
                <th className="text-right p-3 font-medium text-muted-foreground">vs BM Mean</th>
              </tr>
            </thead>
            <tbody>
              {[...(sectors ?? [])].sort((a, b) => compareSectorNames(a.name, b.name)).map((s, idx) => {
                const hyp = hypotheticalBySector.get(s.id) || 0;
                const bench = benchmarkBySectorName.get(s.name) || 0;
                const bmMean = bmHouseMeanBySectorName.get(s.name) || 0;
                const vsBench = hyp - bench;
                const vsBM = hyp - bmMean;
                const showBench = bench > 0 || hyp > 0;
                const showBM = selectedHouses.length > 0 && (bmMean > 0 || hyp > 0);
                return (
                  <tr key={s.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="px-3 py-1 text-xs font-medium">
                      {s.name}
                    </td>
                    <td className="px-3 py-1 text-right text-xs font-mono">
                      {hyp > 0 ? hyp.toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-1 text-right text-xs font-mono",
                        showBench && vsBench > 0 && "text-emerald-600",
                        showBench && vsBench < 0 && "text-red-600"
                      )}
                    >
                      {showBench ? `${vsBench >= 0 ? "+" : ""}${vsBench.toFixed(2)}` : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-1 text-right text-xs font-mono",
                        showBM && vsBM > 0 && "text-emerald-600",
                        showBM && vsBM < 0 && "text-red-600"
                      )}
                    >
                      {showBM ? `${vsBM >= 0 ? "+" : ""}${vsBM.toFixed(2)}` : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 bg-muted/40 font-semibold">
                <td className="p-3 text-xs">Total</td>
                <td className="p-3 text-right text-xs font-mono">
                  {Array.from(hypotheticalBySector.values()).reduce((a, b) => a + b, 0).toFixed(2)}
                </td>
                <td className="p-3" />
                <td className="p-3" />
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      <RRebalanceNotesDialog open={notesOpen} onOpenChange={setNotesOpen} rebalanceDate={notesDate} />
    </div>
  );
}

export default function RHypotheticalCalculator() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Hypothetical Asset Allocation Calculator</h1>
        <p className="text-sm text-muted-foreground">
          All five R portfolios are shown below. Adjust fund weights in any portfolio to model resulting CBAM sector
          allocations. Compare against the R-portfolio benchmark and the mean of selected BM fund houses.
        </p>
      </div>

      {PORTFOLIOS.map((p) => (
        <section key={p} className="space-y-3">
          <div className="flex items-center gap-3 sticky top-0 z-20 bg-background/95 backdrop-blur py-2 border-b">
            <h2 className="text-lg font-semibold text-foreground">R{p}</h2>
            <span className="text-xs text-muted-foreground">Hypothetical calculator</span>
          </div>
          <PortfolioCalculator portfolio={p} />
        </section>
      ))}
    </div>
  );
}
